<template>
    <validation-observer ref="form" v-slot="{ meta, errors, handleSubmit }">
        <b-form @submit.prevent="handleSubmit(_, save)">
            <busy-modal :show="loading" :overlay="true"></busy-modal>
            <b-row>
                <b-col md="6">
                    <validation-provider name="login_email" v-slot="{ handleChange }" rules="required|email">
                        <form-input
                            floating-label="Login Email"
                            type="email"
                            v-model="values.login_email"
                            :left-icon="['fas', 'envelope']"
                            :error="errors.login_email"
                            @input="handleChange"
                        ></form-input>
                    </validation-provider>
                </b-col>
                <b-col md="6">
                    <validation-provider name="email" v-slot="{ handleChange }" rules="email">
                        <form-input
                            floating-label="Contact Email"
                            type="email"
                            v-model="values.email"
                            :left-icon="['fas', 'envelope']"
                            :error="errors.email"
                            @input="handleChange"
                        ></form-input>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="2">
                    <validation-provider name="title">
                        <form-select
                            floating-label="Title"
                            v-model="values.title"
                            :left-icon="['fas', 'user']"
                            :options="titleOptions"
                            :error="errors.title"
                        ></form-select>
                    </validation-provider>
                </b-col>
                <b-col md="4">
                    <validation-provider name="first_name" v-slot="{ handleChange }" rules="required">
                        <form-input
                            floating-label="First Name"
                            v-model="values.first_name"
                            :left-icon="['fas', 'user']"
                            :error="errors.first_name"
                            @input="handleChange"
                        ></form-input>
                    </validation-provider>
                </b-col>
                <b-col md="6">
                    <validation-provider name="last_name" v-slot="{ handleChange }" rules="required">
                        <form-input
                            floating-label="Last Name"
                            v-model="values.last_name"
                            :left-icon="['fas', 'user']"
                            :error="errors.last_name"
                            @input="handleChange"
                        ></form-input>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="6">
                    <validation-provider name="company">
                        <form-input
                            floating-label="Company"
                            v-model="values.company"
                            :left-icon="['fas', 'building']"
                            :error="errors.company"
                        ></form-input>
                    </validation-provider>
                </b-col>
                <b-col md="6" v-if="values.id">
                    <validation-provider name="unique_code">
                        <form-input
                            floating-label="Code"
                            :description="uniqueCodeDescription"
                            v-model="values.unique_code"
                            :left-icon="['fas', 'fingerprint']"
                            :error="errors.unique_code"
                            disabled
                        ></form-input>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row v-if="values.id">
                <b-col md="6">
                    <input type="text" style="display: none" />
                    <input type="password" style="display: none" />
                    <validation-provider name="password">
                        <form-input
                            autocomplete="new-password"
                            floating-label="Password"
                            type="password"
                            description="Fill in to change password, otherwise leave empty."
                            v-model="values.password"
                            :left-icon="['fas', 'lock']"
                            :error="errors.password"
                        ></form-input>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="6">
                    <validation-provider name="address.address1">
                        <form-input
                            floating-label="Address1"
                            v-model="values.address.address1"
                            :left-icon="['fas', 'location-dot']"
                            :error="errors['address.address1']"
                        ></form-input>
                    </validation-provider>
                </b-col>
                <b-col md="6">
                    <validation-provider name="address.address2">
                        <form-input
                            floating-label="Address2"
                            v-model="values.address.address2"
                            :left-icon="['fas', 'location-dot']"
                            :error="errors['address.address2']"
                        ></form-input>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="4">
                    <validation-provider name="address.country" v-slot="{ handleChange }" rules="required">
                        <form-select
                            floating-label="Country"
                            v-model="values.address.country"
                            :left-icon="['fas', 'location-dot']"
                            :options="countryOptions"
                            :error="errors['address.country']"
                            @change="handleChange"
                        ></form-select>
                    </validation-provider>
                </b-col>
                <b-col md="2">
                    <validation-provider name="address.zip">
                        <form-input
                            floating-label="Zip"
                            v-model="values.address.zip"
                            :left-icon="['fas', 'location-dot']"
                            :error="errors['address.zip']"
                            @change="handleZipChange"
                        ></form-input>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="6">
                    <validation-provider name="address.city">
                        <form-input
                            floating-label="City"
                            v-model="values.address.city"
                            :left-icon="['fas', 'location-dot']"
                            :error="errors['address.city']"
                        ></form-input>
                    </validation-provider>
                </b-col>
                <b-col md="6">
                    <validation-provider name="address.state">
                        <form-select
                            floating-label="State/Region"
                            v-model="values.address.state"
                            :left-icon="['fas', 'location-dot']"
                            :options="stateOptions"
                            :error="errors['address.state']"
                        ></form-select>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row v-if="provinces.length > 0">
                <b-col md="6">
                    <validation-provider name="address.province">
                        <form-select
                            floating-label="Province"
                            v-model="values.address.province"
                            :left-icon="['fas', 'location-dot']"
                            :options="provinceOptions"
                            :error="errors['address.province']"
                        ></form-select>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="6">
                    <validation-provider name="address.phone" v-slot="{ handleChange }">
                        <form-phone-number-input
                            inputId="owner-phone-number"
                            floating-label="Phone"
                            v-model="values.address.phone"
                            :default-country-code="defaultCountryCode"
                            :error="errors['address.phone']"
                            @input="handleChange"
                        ></form-phone-number-input>
                    </validation-provider>
                </b-col>
                <b-col md="6">
                    <validation-provider name="mobile_phone" v-slot="{ handleChange }">
                        <form-phone-number-input
                            inputId="owner-mobile-phone-number"
                            floating-label="Mobile Phone"
                            v-model="values.mobile_phone"
                            :default-country-code="defaultCountryCode"
                            :error="errors['mobile_phone']"
                            @input="handleChange"
                        ></form-phone-number-input>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="6">
                    <validation-provider name="address.work_phone" v-slot="{ handleChange }">
                        <form-phone-number-input
                            inputId="owner-work-phone-number"
                            floating-label="Work Phone"
                            v-model="values.address.work_phone"
                            :default-country-code="defaultCountryCode"
                            :error="errors['address.work_phone']"
                            @input="handleChange"
                        ></form-phone-number-input>
                    </validation-provider>
                </b-col>
                <b-col md="6">
                    <validation-provider name="address.fax" v-slot="{ handleChange }">
                        <form-phone-number-input
                            inputId="owner-fax-number"
                            floating-label="Fax"
                            v-model="values.address.fax"
                            :default-country-code="defaultCountryCode"
                            :error="errors['address.fax']"
                            @input="handleChange"
                        ></form-phone-number-input>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="6">
                    <validation-provider name="tax_id">
                        <form-input
                            floating-label="Tax ID"
                            :description="taxIDDescription"
                            v-model="values.tax_id"
                            :left-icon="['fas', 'dollar-sign']"
                            :error="errors['tax_id']"
                        ></form-input>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="6">
                    <validation-provider name="pay_to">
                        <form-textarea
                            floating-label="Pay To"
                            :description="payToDescription"
                            v-model="values.pay_to"
                            :left-icon="['fas', 'user']"
                            :error="errors['pay_to']"
                        ></form-textarea>
                    </validation-provider>
                </b-col>
                <b-col md="6">
                    <validation-provider name="notes">
                        <form-textarea
                            floating-label="Internal Notes"
                            v-model="values.notes"
                            :left-icon="['fas', 'note-sticky']"
                            :error="errors['notes']"
                        ></form-textarea>
                    </validation-provider>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="6">
                    <form-checkbox v-model="values.is_active"> Active </form-checkbox>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="6">
                    <form-checkbox v-model="values.allow_edit_profile"> Allow edit profile </form-checkbox>
                </b-col>
            </b-row>
            <b-row v-if="!values.id">
                <b-col md="6">
                    <form-checkbox v-model="values.send_email"> Send owner login credentials via email </form-checkbox>
                </b-col>
            </b-row>
            <b-button class="mt-4 mb-4" type="submit" variant="primary" :disabled="loading || !meta.valid">
                Save
            </b-button>
        </b-form>
    </validation-observer>
</template>

<script>
    import isEmpty from 'lodash/isEmpty';
    import isNil from 'lodash/isNil';
    import get from 'lodash/get';
    import { mapGetters } from 'vuex';
    import API from '../../../api';
    import { formatResponseError } from '../../../utils/api';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import { defineAsyncComponent } from 'vue';

    const FormPhoneNumberInput = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-form-phone-number-input" */
            '../../generic/form-phone-number-input/form-phone-number-input.vue'
        )
    );

    export default {
        name: 'OwnerDetailsForm',
        components: {
            BusyModal,
            FormCheckbox,
            FormInput,
            FormPhoneNumberInput,
            FormTextarea,
            FormSelect,
        },
        props: {
            initialValues: {
                type: Object,
                default: () => ({}),
            },
        },
        computed: {
            ...mapGetters('dictionary', ['titles']),
            ...mapGetters('owner', ['metadata']),
            defaultCountryCode() {
                return get(this.values, 'address.country.code', this.metadata.default_country_code);
            },
            countryOptions() {
                return [
                    ...this.countries.map((country) => ({
                        value: country,
                        text: country.name,
                    })),
                ];
            },
            stateOptions() {
                return [
                    { value: null, text: 'Please select state' },
                    ...this.states.map((state) => ({
                        value: state,
                        text: state.name,
                    })),
                ];
            },
            provinceOptions() {
                return [
                    { value: null, text: 'Please select province' },
                    ...this.provinces.map((province) => ({
                        value: province,
                        text: province.name,
                    })),
                ];
            },
            titleOptions() {
                return [
                    { value: null, text: '' },
                    ...this.titles.map((title) => ({
                        value: title.id,
                        text: title.name,
                    })),
                ];
            },
        },
        data() {
            return {
                loading: false,
                countries: [],
                states: [],
                provinces: [],
                uniqueCodeDescription:
                    "Unique code. Best option is five characters of owner's" + ' last name and two of the first name.',
                taxIDDescription:
                    "Enter either the owner's Federal Tax ID number or" +
                    " social security number. This will be used on 1099's.",
                payToDescription:
                    'These two lines provide for checks to be made out to individuals ' +
                    'other than the owner in the name field, or for checks to be made out to multiple owners.',
                values: this.getInitialValues(),
            };
        },
        mounted() {
            (async () => {
                this.loading = true;
                try {
                    await Promise.all([
                        await this.fetchDictionaryTitles(),
                        await this.fetchMetadata(),
                        await this.fetchCountries(),
                        await this.fetchStates(),
                        await this.fetchProvinces(),
                    ]);
                    this.setDefaultCountry();
                    this.$refs.form.setValues(this.values);
                } finally {
                    this.loading = false;
                }
            })();
        },
        methods: {
            getAddressValue(address) {
                return {
                    address1: address.address1 || '',
                    address2: address.address2 || '',
                    country: address.country || null,
                    city: address.city || '',
                    state: address.state || null,
                    province: address.province || null,
                    zip: address.zip || '',
                    phone: address.phone || '',
                    work_phone: address.work_phone || '',
                    fax: address.fax || '',
                };
            },
            getInitialValues() {
                return {
                    id: this.initialValues.id || null,
                    first_name: this.initialValues.first_name || '',
                    last_name: this.initialValues.last_name || '',
                    is_active: this.initialValues.is_active || false,
                    allow_edit_profile: !isNil(this.initialValues.allow_edit_profile)
                        ? this.initialValues.allow_edit_profile
                        : true,
                    login_email: this.initialValues.login_email || '',
                    email: this.initialValues.email || '',
                    title: this.initialValues.title || null,
                    unique_code: this.initialValues.unique_code || '',
                    company: this.initialValues.company || '',
                    address: this.getAddressValue(this.initialValues.address || {}),
                    mobile_phone: this.initialValues.mobile_phone || '',
                    tax_id: this.initialValues.tax_id || '',
                    pay_to: this.initialValues.pay_to || '',
                    notes: this.initialValues.notes || '',
                    send_email: this.initialValues.send_email || false,
                    password: null,
                };
            },
            setDefaultCountry() {
                if (this.metadata.default_country_code && !this.values.address.country) {
                    const country = this.countries.find(
                        (country) => country.code === this.metadata.default_country_code
                    );
                    if (country) {
                        this.values.address.country = country;
                    }
                }
            },
            async handleZipChange(zip) {
                if (!this.values.address || !this.values.address.country || zip.length < 5) {
                    return;
                }

                this.loading = true;
                try {
                    const { data } = await API.get(`/asynchronous/ziptastic/${this.values.address.country.id}/${zip}`);
                    if (!data || data.message === 'Not Found') {
                        throw new Error();
                    }
                    this.values.address.city = data.city;
                    this.states.forEach((state) => {
                        if (state.code === data.state_short) {
                            this.values.address.state = state;
                        }
                    });
                } catch (err) {
                    this.errorToast({ text: 'Unable to auto fill city and state.' });
                } finally {
                    this.loading = false;
                }
            },
            async fetchCountries() {
                try {
                    const { data } = await API.get('/app/country/');
                    this.countries = data;
                } catch (err) {
                    this.errorToast({ text: 'Could not load country data' });
                }
            },
            async fetchStates() {
                if (!this.values.address.country) {
                    return;
                }

                try {
                    const { data } = await API.get('/app/state/', {
                        params: {
                            country: this.values.address.country.id,
                        },
                    });
                    this.states = data;
                } catch (err) {
                    this.errorToast({ text: 'Could not load state data.' });
                }
            },
            async fetchProvinces() {
                if (!this.values.address.state) {
                    return;
                }

                try {
                    const { data } = await API.get('/app/province/', {
                        params: {
                            state: this.values.address.state.id,
                        },
                    });
                    this.provinces = data;
                } catch (err) {
                    this.errorToast({ text: 'Could not load province data.' });
                }
            },
            async fetchDictionaryTitles() {
                try {
                    await this.$store.dispatch('dictionary/fetchTitles');
                } catch (err) {
                    this.errorToast({ text: 'Could not load title data.' });
                }
            },
            async fetchMetadata() {
                try {
                    await this.$store.dispatch('owner/fetchMetadata');
                } catch (err) {
                    this.errorToast({ text: 'Could not load meta data.' });
                }
            },
            async update() {
                await this.$store.dispatch('owner/updateOwner', this.values);
                this.successToast({ text: 'Owner was successfully updated.' });
            },
            async create() {
                const owner = await this.$store.dispatch('owner/createOwner', this.values);
                this.$emit('success-create', owner);
            },
            async save() {
                this.loading = true;

                try {
                    await (this.initialValues.id ? this.update() : this.create());
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);

                    if (generic) {
                        this.errorToast({ text: generic });
                    }
                    if (!isEmpty(fields)) {
                        this.$refs.form.setErrors(fields);
                    }
                } finally {
                    this.loading = false;
                }
            },
        },
        watch: {
            initialValues() {
                this.values = this.getInitialValues();
            },
            'values.address.country': function (country, previousCountry) {
                const countryID = country && country.id;
                const previousCountryID = previousCountry && previousCountry.id;
                if (countryID !== previousCountryID) {
                    this.fetchStates();
                }
            },
            'values.address.state': function (state, previousState) {
                const stateID = state && state.id;
                const previousStateID = previousState && previousState.id;
                if (stateID !== previousStateID) {
                    this.fetchProvinces();
                }
            },
        },
    };
</script>
